import { FC } from "react";

import { Optional } from "common/utils/types";
import {
  IAddress,
  IPatient,
  IProfile as ICommonProfile,
  CardPaymentMethod,
  UploadedImage,
  IPatientWrite,
  IPatientWriteBody,
  ClientUserWithContacts,
  ClientEmailAddress,
  ClientCommunicationNumber,
} from "common/types";

import { ModalStatus } from "client/common/types";

export interface IProfile extends ICommonProfile {}

export enum AuthenticationStep {
  Lookup = "lookup",
  SmsCode = "sms-code",
  Password = "password",
  Signup = "signup",
}

/** Matches accounts constants ClientAuthSource */
export enum AuthenticationSource {
  MagicLink = "MAGIC_LINK",
  AutoAuth = "AUTO_AUTH",
  Hijack = "HIJACK",
  SignupAPI = "SIGNUP_API",
  LoginAPI = "LOGIN_API",
}

/** Matches accounts constants AuthenticationRedirectAfterLookup */
export enum AuthenticationRedirectAfterLookup {
  SigninPhone = "SIGNIN_PHONE",
  SigninEmail = "SIGNIN_EMAIL",
  Verification = "VERIFICATION",
}

/** Matches accounts SignupProfileNameValidationSerializer */
export interface IProfileName {
  first_name: string;
  last_name: string;
}

/** Matches accounts ClientProfileLookupSerializer */
export interface IProfileLookup {
  number?: number;
  email?: string;
  check_sms: boolean;
}

/** Matches accounts ClientProfileLookupView POST response */
export interface IProfilePhoneLookupResult {
  number: number;
  email: never;
  authentication_redirect: AuthenticationRedirectAfterLookup;
}

export interface IProfileEmailLookupResult {
  number: never;
  email: string;
  authentication_redirect: AuthenticationRedirectAfterLookup;
}

export type IProfileLookupResult = IProfileEmailLookupResult | IProfilePhoneLookupResult;

interface ISendSmsVerificationCode {
  number: number;
  email?: never;
  profile?: VerifyCodeProfile;
}

interface ISendEmailVerificationCode {
  number?: never;
  email: string;
  profile?: VerifyCodeProfile;
}

/** Matches accounts ClientSendVerificationCodeView POST request  */
export type ISendVerificationCode = ISendSmsVerificationCode | ISendEmailVerificationCode;

/** matches accounts SMSVerifyCodeProfileSerializer */
export interface VerifyCodeProfile extends IProfileName {
  new_password: string;
}

/** Matches accounts ClientVerifySignUpView POST request */
export interface VerifyCodeSignup {
  number: number;
  email: string;
  code: string;
  profile: VerifyCodeProfile;
}

/** Matches accounts ClientVerifySignUpView POST response */
export interface IVerifySmsCodeSignupResult {
  token: string;
}

export interface IPasswordLogin {
  username: string;
  password: string;
}

/** Matches clients ClientProfileDetailSerializer */
export interface IClient {
  id: number;
  all_owner_names_display: string;
  patients: ClientDetailPatient[];
  primary_client_user: ClientUserWithContacts;
  primary_address?: IAddress;
  addresses: IClientProfileListAddress[];
  primary_payment_method: Optional<CardPaymentMethod>;
  payment_methods: ClientProfileListPaymentMethod[];
  first_subscription_eligible_for_clinic: boolean;
  recurring_subscription_eligible_for_clinic: boolean;
  is_employee: boolean;
  receives_employee_pricing: boolean;
  primary_contact_email: Optional<ClientEmailAddress>;
  primary_contact_number: Optional<ClientCommunicationNumber>;
}
export type ClientDetailPatient = IPatient & {
  image: UploadedImage | null;
};

/** Matches accounts ClientProfileAddressListSerializer */
export interface IClientProfileListAddress extends IAddress {
  active_subscription_count: number;
  paused_subscription_count: number;
}

/** matches accounts ClientProfilePaymentMethodSerializer */
export interface ClientProfileListPaymentMethod extends CardPaymentMethod {
  relevant_subscription_count: number;
}

export type SignupFormValues = {
  phone: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
};

export enum ActivityType {
  Simple = "SIMPLE",
  Subscription = "SUBSCRIPTION",
  Order = "ORDER",
}

interface IActivityContent {
  type: ActivityType;
  label: string;
}

interface ISimpleActivityContent extends IActivityContent {
  type: ActivityType.Simple;
  description?: string;
}

interface ISubscriptionActvityContent extends IActivityContent {
  type: ActivityType.Subscription;
  subscription_id: number;
  product_name: string;
  arrival_date: string;
}

interface IOrderActivityContent extends IActivityContent {
  type: ActivityType.Order;
  order_id: number;
  items: string[];
  placed_date: string;
}

export interface IRecentActivity {
  id: number;
  is_active: boolean;
  content: ISimpleActivityContent | ISubscriptionActvityContent | IOrderActivityContent;
}

export type AccountFormProps = { onSuccess: () => void };
export type NewAddressFormProps = AccountFormProps & { displaySubscriptionsBulkUpdate?: boolean };
export type AccountEntityUpdateFormProps = AccountFormProps & { id: number };

type AccountFormComponent = FC<AccountFormProps>;
type AccountEntityUpdateFormComponent = FC<AccountEntityUpdateFormProps>;

type AccountFormLookupInstance<T> = { label: string; Form: T };

export type AccountFormLookup = {
  updatePhone: AccountFormLookupInstance<AccountFormComponent>;
  updateCommsPhone: AccountFormLookupInstance<AccountFormComponent>;
  updateEmail: AccountFormLookupInstance<AccountFormComponent>;
  updateCommsEmail: AccountFormLookupInstance<AccountFormComponent>;
  updateName: AccountFormLookupInstance<AccountFormComponent>;
  updateAccount: AccountFormLookupInstance<AccountFormComponent>;
  updatePassword: AccountFormLookupInstance<AccountFormComponent>;
  updateCommunicationPreferences: AccountFormLookupInstance<AccountFormComponent>;
  updateControlledSubstancesFields: AccountFormLookupInstance<AccountFormComponent>;
  updateAddress: AccountFormLookupInstance<AccountEntityUpdateFormComponent>;
  updatePayment: AccountFormLookupInstance<AccountEntityUpdateFormComponent>;
  updatePet: AccountFormLookupInstance<AccountEntityUpdateFormComponent>;
  newAddress: null;
  newPayment: AccountFormLookupInstance<AccountFormComponent>;
  newPet: AccountFormLookupInstance<AccountFormComponent>;
};

export enum AccountForm {
  UpdatePhone = "updatePhone",
  UpdateCommsPhone = "updateCommsPhone",
  UpdateEmail = "updateEmail",
  UpdateCommsEmail = "updateCommsEmail",
  UpdateName = "updateName",
  UpdatePassword = "updatePassword",
  UpdateCommunicationPreferences = "updateCommunicationPreferences",
  UpdateControlledSubstancesFields = "updateControlledSubstancesFields",
  UpdateAddress = "updateAddress",
  UpdatePayment = "updatePayment",
  UpdatePet = "updatePet",
  NewAddress = "newAddress",
  NewPayment = "newPayment",
  NewPet = "newPet",
}

export type AccountSearch = {
  form: keyof AccountFormLookup | "0";
  visible: ModalStatus;
  id: string;
};

/** matches ClientProfilePasswordUpdateSerializer */
export type PasswordChangeFormValues = {
  old_password: string;
  new_password: string;
};

/* matches accounts ClientResetPasswordView POST body */
export type ResetPasswordRequest = {
  username: string;
};

type PhoneResetPasswordConfirm = {
  phone_number: string;
  email: never;
  user_email_exists: boolean;
};

type EmailResetPasswordConfirm = {
  phone_number: never;
  email: string;
  user_email_exists: never;
};

/* matches accounts ClientResetPasswordView POST response */
export type ResetPasswordConfirm = PhoneResetPasswordConfirm | EmailResetPasswordConfirm;

/** Matches accounts VerifyLoginPhoneNumberView GET params */
export interface LoginPhoneValidationSearch {
  number: string;
}

/** Matches accounts VerifyLoginPhoneNumberView GET response */
export interface ILoginPhoneValidationResults {
  is_valid: boolean;
  detail: string;
}

/** Matches accounts SMSVerifyCodeUpdateClientNumberSerializer */
export type PrimaryPhoneMutation = {
  number: number;
  code: string;
  set_primary_on_client_only: boolean;
};

/** Matches accounts ClientUpdateSMSNumberView.post */
export type PrimaryPhone = {
  number: number;
};

/** Query Params used on the auth pages to help share state */
export type AuthSearch = Partial<{
  next: string;
  phone: string;
  email: string;
  first_name: string;
  last_name: string;
  flash: string;
  method: string;
}>;

/** Matches accounts ClientLoginErrorKind */
export enum LoginErrorKind {
  NoUser = "NO_USER",
  InvalidUsername = "INVALID_USERNAME",
  InvalidPassword = "INVALID_PASSWORD",
  AttachedToOtherAcct = "ATTACHED_TO_OTHER_ACCT",
  WrongPassword = "WRONG_PASSWORD",
  UsernameShouldBeEmail = "USERNAME_SHOULD_BE_EMAIL",
  UsernameShouldBeNumber = "USERNAME_SHOULD_BE_NUMBER",
}

/** Matches accounts LoginValidationError */
export type LoginErrorData = { kind?: LoginErrorKind };

export type PasswordCondition = {
  check: (password: string) => boolean;
  label: string;
};

/** Matches ClientPatientDetailSerializer */
export type PatientDetail = IPatient & {
  image: UploadedImage | null;
};

/** Matches ClientPatientDetailSerializer _writable_fields */
export type PatientUpdate = IPatientWrite & {
  image_id?: number | null;
  should_deactivate_client_relationship?: boolean;
};

export type PatientUpdateBody = IPatientWriteBody & {
  image_id?: number | null;
};
